import isEqual from 'lodash/isEqual';
import cloneDeep from 'lodash/cloneDeep';
import isBoolean from 'lodash/isBoolean';
import PropTypes from 'prop-types';

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Random } from '@zedoc/random';

import { default as OriginalFilters } from './elements/Filters';

export const FILTER_CATEGORY__DASHBOARD = 'dashboard';
export const FILTER_CATEGORY__VARIABLE = 'variable';
export const FILTER_CATEGORY__QUESTIONNAIRE = 'questionnaire';
export const FILTER_CATEGORY__OTHER = 'other';
export const FILTER_CATEGORIES = [
  FILTER_CATEGORY__DASHBOARD,
  FILTER_CATEGORY__QUESTIONNAIRE,
  FILTER_CATEGORY__VARIABLE,
  FILTER_CATEGORY__OTHER,
];

const Filters = ({
  presets,
  value,
  fixedFirstFilterKey,
  allowConditionGroups,
  simpleFilters,
  onChange,
  onSubmit,
  optionsDisplayCounts,
  optionsSelector,
  optionsSubscription,
  onResetSavedFilters,
  onFiltersDraftChange,
  extra,
}) => {
  const [key, setKey] = useState();
  const [submitFailed, setSubmitFailed] = useState(false);
  const [isAdvancedView, setIsAdvancedView] = useState(null);

  const handleOnReset = useCallback(() => {
    onChange();
    onResetSavedFilters();
    // NOTE: Force re-rendering <Filters> component, to make sure
    //       that the local state stays in sync.
    setKey(Random.id());
  }, [setKey, onChange, onResetSavedFilters]);

  const setSimpleFilters = useCallback(() => {
    onChange(cloneDeep(simpleFilters));
    // NOTE: Force re-rendering <Filters> component, to make sure
    //       that the local state stays in sync.
    setKey(Random.id());
  }, [setKey, onChange, simpleFilters]);

  const handleShowFilters = () => {
    setIsAdvancedView(true);
    handleOnReset();
  };

  const handleHideFilters = useCallback(() => {
    setIsAdvancedView(false);
    setSimpleFilters();
  }, [setSimpleFilters]);

  const prevSimpleFiltersRef = useRef();
  useEffect(() => {
    if (!isEqual(simpleFilters, prevSimpleFiltersRef.current)) {
      prevSimpleFiltersRef.current = simpleFilters;

      setIsAdvancedView(simpleFilters ? simpleFilters.length === 0 : true);
      if (simpleFilters !== null) {
        setSimpleFilters();
      }
    }
  }, [simpleFilters, setSimpleFilters]);

  return isBoolean(isAdvancedView) ? (
    <OriginalFilters
      key={key}
      presets={presets}
      value={value}
      fixedFirstFilterKey={fixedFirstFilterKey}
      allowConditionGroups={allowConditionGroups}
      onChange={onChange}
      onSubmit={onSubmit}
      optionsDisplayCounts={optionsDisplayCounts}
      optionsSelector={optionsSelector}
      optionsSubscription={optionsSubscription}
      onReset={isAdvancedView ? handleOnReset : setSimpleFilters}
      onDelete={handleOnReset}
      submitFailed={submitFailed}
      setSubmitFailed={setSubmitFailed}
      extra={extra}
      isAdvancedView={isAdvancedView}
      onShowFilters={handleShowFilters}
      onHideFilters={handleHideFilters}
      hasSimpleView={!!simpleFilters}
      onFiltersDraftChange={onFiltersDraftChange}
    />
  ) : null;
};

Filters.propTypes = {
  presets: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.string,
      name: PropTypes.string,
    }),
  ),
  value: PropTypes.arrayOf(PropTypes.objectOf(PropTypes.any)),
  fixedFirstFilterKey: PropTypes.string,
  allowConditionGroups: PropTypes.bool,
  simpleFilters: PropTypes.arrayOf(PropTypes.objectOf(PropTypes.any)),
  onChange: PropTypes.func.isRequired,
  onSubmit: PropTypes.func,
  optionsDisplayCounts: PropTypes.bool,
  optionsSubscription: PropTypes.func,
  optionsSelector: PropTypes.shape({
    all: PropTypes.func,
  }),
  extra: PropTypes.node,
  onResetSavedFilters: PropTypes.func,
  onFiltersDraftChange: PropTypes.func,
};

Filters.defaultProps = {
  presets: [],
  value: null,
  fixedFirstFilterKey: null,
  allowConditionGroups: true,
  simpleFilters: null,
  onSubmit: () => {},
  optionsDisplayCounts: false,
  optionsSubscription: null,
  optionsSelector: null,
  extra: null,
  onResetSavedFilters: () => {},
  onFiltersDraftChange: null,
};

export default Filters;
